/**
 * Scores Page
 * Phase 7: Audit scoring overview with explainability, breakdown, and severity distribution.
 * 
 * Displays trustworthy audit scores based on real backend data:
 * - Risk Score: Weighted by severity and confidence
 * - Debt Score: Code quality findings
 * - Compliance Score: Track coverage minus critical penalty
 * 
 * All scores are deterministic and explainable from backend formulas.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { api, ScoringResult, AuditDetailsResponse, AuditRunListItem } from '../lib/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ScoreCard } from '../components/ScoreCard'
import { ScoreBreakdownPanel } from '../components/ScoreBreakdownPanel'
import { SeverityDistributionCard } from '../components/SeverityDistributionCard'
import { EmptyState } from '../components/design-system/EmptyState'
import { StatusBadge } from '../components/design-system/Badge'
import { useLocale } from '../contexts/LocaleContext'
import { trackLabel } from '../lib/i18n'

export function ScoresPage() {
  const { t, locale } = useLocale()
  const { auditId } = useParams<{ auditId: string }>()
  const [searchParams] = useSearchParams()
  const auditRunId = auditId || searchParams.get('audit_run_id')
  
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [scoring, setScoring] = useState<ScoringResult | null>(null)
  const [audit, setAudit] = useState<AuditDetailsResponse | null>(null)
  const [runs, setRuns] = useState<AuditRunListItem[]>([])
  
  useEffect(() => {
    if (auditRunId) {
      loadScores()
      return
    }
    let cancelled = false
    async function loadIndex() {
      try {
        setLoading(true)
        setError(null)
        const data = await api.listAuditRuns({ page: 1, page_size: 50 })
        if (!cancelled) setRuns(data.audits)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t('scores.failed'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    loadIndex()
    return () => {
      cancelled = true
    }
  }, [auditRunId])
  
  async function loadScores() {
    if (!auditRunId) return
    
    try {
      setLoading(true)
      setError(null)
      
      // Load scores and audit details in parallel
      const [scoringData, auditData] = await Promise.all([
        api.getAuditScores(auditRunId),
        api.getAuditDetails(auditRunId),
      ])
      
      setScoring(scoringData)
      setAudit(auditData)
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('cannot be scored')) {
          setError(t('scores.cannotScore'))
        } else if (err.message.includes('not found')) {
          setError(t('scores.notFound'))
        } else {
          setError(err.message)
        }
      } else {
        setError(t('scores.failed'))
      }
    } finally {
      setLoading(false)
    }
  }
  
  if (loading) {
    return (
      <div className="space-y-6" data-testid="scores-page">
        <h1 className="text-2xl font-medium mb-1">{t('scores.title')}</h1>
        <LoadingSpinner message={t('scores.loading')} />
      </div>
    )
  }

  if (!auditRunId) {
    const scored = runs.filter((run) =>
      ['completed', 'partial'].includes(run.status)
    )
    return (
      <div className="space-y-6" data-testid="scores-page">
        <header>
          <h1 className="text-2xl font-medium mb-1">{t('scores.title')}</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {t('scores.indexLead')}
          </p>
        </header>
        {error && <div className="card card-error"><p className="text-sm">{error}</p></div>}
        {scored.length === 0 ? (
          <EmptyState
            title={t('scores.noneYet')}
            description={t('scores.noneYetBody')}
            action={{ label: t('scores.goAudits'), onClick: () => navigate('/audits') }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {scored.map((run) => (
              <button
                key={run.id}
                onClick={() => navigate(`/scores?audit_run_id=${run.id}`)}
                className="card"
                style={{ textAlign: 'left', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                  <strong>{run.project_name}</strong>
                  <StatusBadge status={run.status} />
                </div>
                <div className="text-sm text-[var(--text-secondary)]">
                  {t('scores.riskDebtCompliance', {
                    risk: run.risk_score ?? '—',
                    debt: run.debt_score ?? '—',
                    compliance: run.compliance_score ?? '—',
                  })}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="card card-error">
        <p className="text-sm mb-4">{error}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-sm btn-primary"
        >
          {t('scores.backDash')}
        </button>
      </div>
    )
  }
  
  if (!scoring || !audit) {
    return (
      <div className="card">
        <p className="text-sm text-[var(--text-secondary)]">
          {t('scores.noData')}
        </p>
      </div>
    )
  }
  
  const hasAnyScore = 
    scoring.risk_score !== null ||
    scoring.debt_score !== null ||
    scoring.compliance_score !== null
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/audit/${auditRunId}`)}
          className="btn btn-sm btn-secondary"
        >
          {t('scores.backAudit')}
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-medium mb-1">{t('scores.auditTitle')}</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {audit.project_name} • {new Date(scoring.scored_at).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US')}
          </p>
        </div>
      </div>
      
      {!hasAnyScore && (
        <div className="card bg-[var(--surface-secondary)]">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-[var(--accent)] flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <div className="text-sm font-medium text-[var(--text-primary)] mb-1">
                {t('scores.notEvaluatedTitle')}
              </div>
              <div className="text-sm text-[var(--text-secondary)]">
                {t('scores.noFindingsExplain')}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Score Overview */}
      <div className="grid gap-4 md:grid-cols-3">
        <ScoreCard
          label={t('scores.risk')}
          score={scoring.risk_score}
          type="risk"
          explanation={t('scores.riskExplain')}
        />
        
        <ScoreCard
          label={t('scores.debt')}
          score={scoring.debt_score}
          type="debt"
          explanation={t('scores.debtExplain')}
        />
        
        <ScoreCard
          label={t('scores.compliance')}
          score={scoring.compliance_score}
          type="compliance"
          explanation={t('scores.complianceExplain')}
        />
      </div>
      
      {/* Score Breakdowns */}
      {scoring.risk_breakdown && (
        <ScoreBreakdownPanel
          breakdown={scoring.risk_breakdown}
          scoreType="Risk"
          initiallyExpanded={false}
        />
      )}
      
      {scoring.debt_breakdown && (
        <ScoreBreakdownPanel
          breakdown={scoring.debt_breakdown}
          scoreType="Debt"
          initiallyExpanded={false}
        />
      )}
      
      {scoring.compliance_breakdown && (
        <ScoreBreakdownPanel
          breakdown={scoring.compliance_breakdown}
          scoreType="Compliance"
          initiallyExpanded={false}
        />
      )}
      
      {/* Severity Distribution and Track Coverage */}
      <div className="grid gap-4 md:grid-cols-2">
        <SeverityDistributionCard
          distribution={scoring.severity_distribution}
          auditRunId={auditRunId!}
        />
        
        <div className="card">
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">
            {t('scores.trackCoverage')}
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[var(--text-secondary)]">
                {t('scores.coverage')}
              </span>
              <span className="text-lg font-medium text-[var(--text-primary)]">
                {(scoring.track_coverage_ratio * 100).toFixed(0)}%
              </span>
            </div>
            
            <div className="w-full h-2 bg-[var(--surface-secondary)] rounded-sm overflow-hidden">
              <div
                className="h-full bg-[var(--accent)] transition-all duration-300"
                style={{ width: `${scoring.track_coverage_ratio * 100}%` }}
                role="progressbar"
                aria-valuenow={scoring.track_coverage_ratio * 100}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            
            <div className="text-xs text-[var(--text-secondary)]">
              {t('scores.tracksExecutedOf', {
                done: scoring.tracks_executed.length,
                total: scoring.tracks_requested.length,
              })}
            </div>
            
            <div className="pt-3 border-t border-[var(--border)]">
              <div className="text-xs text-[var(--text-secondary)] mb-2">
                {t('scores.executedTracks')}
              </div>
              <div className="flex flex-wrap gap-2">
                {scoring.tracks_executed.length > 0 ? (
                  scoring.tracks_executed.map((track) => (
                    <span
                      key={track}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium bg-[var(--surface-secondary)] text-[var(--text-primary)] rounded"
                    >
                      {locale === 'en' ? track.toUpperCase() : trackLabel(locale, track)}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-[var(--text-tertiary)]">
                    {t('scores.noTracksExecuted')}
                  </span>
                )}
              </div>
            </div>
            
            {scoring.tracks_requested.length > scoring.tracks_executed.length && (
              <div className="pt-3 border-t border-[var(--border)]">
                <div className="text-xs text-[var(--text-tertiary)] mb-2">
                  {t('scores.requestedNotExecuted')}
                </div>
                <div className="flex flex-wrap gap-2">
                  {scoring.tracks_requested
                    .filter((trackName) => !scoring.tracks_executed.includes(trackName))
                    .map((track) => (
                      <span
                        key={track}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium bg-[var(--surface-tertiary)] text-[var(--text-tertiary)] rounded"
                      >
                        {locale === 'en' ? track.toUpperCase() : trackLabel(locale, track)}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => navigate(`/findings?audit_run_id=${auditRunId}`)}
          className="btn btn-primary"
        >
          {t('scores.viewAllFindings')}
        </button>
        
        <button
          onClick={() => navigate(`/reports?audit_run_id=${auditRunId}`)}
          className="btn btn-secondary"
        >
          {t('scores.generateReport')}
        </button>
      </div>
    </div>
  )
}
